import type { Component, Ref } from 'vue'
import type { RouteLocation } from 'vue-router'

import dayjs from 'dayjs'

import type { SystemModule } from '@/config/app'

import { canAccessColleaguesList, useAppConfig, useFeatureFlag } from '@/shared/lib/tenant'

import { useProfile } from '@/entities/profile'

import type { AcceptableAnchorTarget } from './sidebarAnchorTypes'

export interface NavigationItem {
  icon?: Component
  iconClass?: string
  name: string
  path: RouteLocation['path']
  show: boolean
  // Задан только у внешних ссылок: такой пункт открывается в новой вкладке, а не через router
  target?: AcceptableAnchorTarget
}

export interface NavigationItemGroup {
  groupName: string
  items: NavigationItem[]
}

export function useNavigation(isMobile: Ref<boolean>) {
  const { isAdmin, isContentManager, isShopManager, isProbationPassed, profile, role } = storeToRefs(useProfile())
  const isAdminOrShopManager = computed(() => isAdmin.value || isShopManager.value)

  const { t } = useI18n()

  const { enabledSystemModules, meta } = useAppConfig()
  const isModuleEnabled = (module: SystemModule) => enabledSystemModules.includes(module)

  // meta типизирована как Record<string, unknown>, поэтому сужаем до строки явно
  const tncUrl = typeof meta?.tncUrl === 'string' ? meta.tncUrl : ''

  const canViewColleaguesList = useFeatureFlag('canViewColleaguesList')
  const isEmployeesSectionAvailable = computed(() => {
    if (!isModuleEnabled('profiles')) {
      return false
    }

    return canAccessColleaguesList({
      canViewColleaguesList: canViewColleaguesList.value,
      role: role.value,
    })
  })

  const navigation = computed<NavigationItemGroup[]>(() => [
    {
      groupName: '',
      items: [
        {
          name: t('navigation_item_home'),
          iconClass: 'fa-regular fa-house-blank text-[16px]',
          path: '/',
          show: true,
        },
      ],
    },
    {
      groupName: t('navigation_group_company'),
      items: [
        {
          name: t('navigation_item_news'),
          iconClass: 'fa-regular fa-newspaper text-[16px]',
          path: '/news',
          show: isModuleEnabled('news'),
        },
        {
          name: t('navigation_item_announcements'),
          iconClass: 'fa-regular fa-bell text-[18px]',
          path: '/events',
          show: isModuleEnabled('events') && isProbationPassed.value,
        },
        {
          name: t('navigation_item_employees'),
          iconClass: 'fa-regular fa-book-user text-[16px]',
          path: '/profiles',
          show: isEmployeesSectionAvailable.value,
        },
        {
          name: t('navigation_item_metrics'),
          iconClass: 'fa-regular fa-chart-bar text-[16px]',
          path:
            '/metrics' + '?metric=metrics_transfer_balance' + `&year=${dayjs().startOf('year').format('YYYY-MM-DD')}`,
          show: isModuleEnabled('metrics') && isAdmin.value,
        },
      ],
    },
    {
      groupName: t('navigation_group_personal'),
      items: [
        {
          name: t('navigation_item_profile'),
          iconClass: 'fa-regular fa-user text-[16px]',
          path: `/profile/${profile.value?.id}`,
          show: isModuleEnabled('profile'),
        },
        {
          name: t('navigation_item_shop'),
          iconClass: 'fa-regular fa-gifts text-[16px]',
          path: '/shop',
          show: isModuleEnabled('shop') && isProbationPassed.value,
        },
        {
          name: t('navigation_item_orders'),
          iconClass: 'fa-regular fa-bags-shopping text-[16px]',
          path: '/my-orders',
          show: isModuleEnabled('my-orders') && isProbationPassed.value,
        },
        {
          name: t('navigation_item_transactions'),
          iconClass: 'fa-regular fa-book-open-lines text-[16px]',
          path: '/transactions',
          show: isModuleEnabled('transactions'),
        },
      ],
    },
    {
      groupName: t('navigation_group_shop_management'),
      items: [
        {
          name: t('navigation_item_goods'),
          iconClass: 'fa-regular fa-gifts text-[16px]',
          path: '/shop-management',
          show: isModuleEnabled('shop-management') && isAdminOrShopManager.value,
        },
        {
          name: t('navigation_item_orders_admin'),
          iconClass: 'fa-regular fa-bags-shopping text-[16px]',
          path: '/orders-management',
          show: isModuleEnabled('orders-management') && isAdminOrShopManager.value,
        },
        {
          name: t('navigation_item_tags'),
          iconClass: 'fa-regular fa-tags text-[16px]',
          path: '/tags-control',
          show: isModuleEnabled('tags-control') && isAdminOrShopManager.value,
        },
        {
          name: t('profiles_balance_management'),
          iconClass: 'fa-regular fa-piggy-bank text-[18px]',
          path: '/allocate-coins',
          show: isModuleEnabled('allocate-coins') && isAdmin.value,
        },
      ],
    },
    {
      groupName: t('navigation_group_hr'),
      items: [
        {
          name: t('navigation_item_faq'),
          iconClass: 'fa-regular fa-question text-[16px]',
          path: '/faq',
          show: isModuleEnabled('faq'),
        },
        {
          name: t('navigation_item_feedback'),
          iconClass: 'fa-sharp fa-regular fa-comments-question text-[16px]',
          path: '/feedback',
          show: isModuleEnabled('feedback'),
        },
        {
          name: t('navigation_item_feedback_control'),
          iconClass: 'fa-regular fa-memo-pad text-[16px]',
          path: '/control-feedback',
          show: isModuleEnabled('control-feedback') && (isAdmin.value || isContentManager.value),
        },
        {
          name: t('navigation_item_roles_control'),
          iconClass: 'fa-regular fa-users-gear text-[16px]',
          path: '/roles-control',
          show: isModuleEnabled('roles-control') && isAdmin.value,
        },
        {
          name: t('navigation_item_admin_panel'),
          iconClass: 'fa-regular fa-gear text-[16px]',
          path: '/admin',
          show: isModuleEnabled('admin') && isAdmin.value,
        },
        {
          name: t('navigation_item_tnc'),
          iconClass: 'fa-regular fa-file-contract text-[16px]',
          path: tncUrl,
          show: Boolean(tncUrl),
          target: '_blank',
        },
      ],
    },
    {
      groupName: '',
      items: [
        {
          name: t('base_signout'),
          iconClass: 'fa-regular fa-left-from-bracket text-[16px]',
          show: isMobile.value,
          path: '/signout',
        },
      ],
    },
  ])

  return { navigation }
}
